import type {
  AuthorizationSubject,
  PortalCapabilityArea,
} from "@whitehaven/permissions";
import { ContentState } from "@whitehaven/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  captureProtectedStateGeneration,
  isProtectedStateGenerationCurrent,
  registerProtectedStateCleaner,
} from "../../protected-state";
import {
  adminSchedulingAuthorizationKey,
  canReadAdminScheduling,
  canWriteAdminScheduling,
} from "./scheduling-authorization";
import type {
  AdminSchedulingWorkspaceMutationGateway,
  AdminSchedulingWorkspaceReadGateway,
} from "./scheduling-gateway";
import {
  applyConfirmedSchedulingAppointment,
  applySchedulingPreview,
  isAdminSchedulingWorkspaceSnapshot,
  previewSchedulingAppointmentMutation,
  type AdminSchedulingAppointmentMutationRequest,
  type AdminSchedulingUndoGrant,
  type AdminSchedulingView,
  type AdminSchedulingWorkspaceSnapshot,
} from "./scheduling-portal-model";
import { createSchedulingIdempotencyKey } from "./scheduling-route-support";
import {
  AdminSchedulingWorkspaceView,
  type AdminSchedulingWorkspaceFeedback,
} from "./AdminSchedulingWorkspaceView";
import type { AppointmentId } from "./scheduling-contract";

export interface AdminSchedulingWorkspaceRouteProps {
  readonly subject: AuthorizationSubject | null;
  readonly capabilityArea: PortalCapabilityArea;
  readonly view: AdminSchedulingView;
  readonly anchorDate: string;
  readonly readGateway: AdminSchedulingWorkspaceReadGateway | null;
  readonly mutationGateway: AdminSchedulingWorkspaceMutationGateway | null;
}

type WorkspaceLoadState =
  | { readonly kind: "loading" }
  | { readonly kind: "error" }
  | {
      readonly kind: "ready";
      readonly snapshot: AdminSchedulingWorkspaceSnapshot;
    };

function WorkspaceFrame({
  title,
  children,
}: {
  readonly title: string;
  readonly children: React.ReactNode;
}) {
  return (
    <section
      className="portal-page scheduling-page"
      aria-labelledby="portal-title"
    >
      <header className="portal-page__header">
        <p className="portal-kicker">Scheduling workspace</p>
        <h1 id="portal-title" tabIndex={-1} data-main-focus data-route-focus>
          {title}
        </h1>
      </header>
      <div className="portal-page__content">{children}</div>
    </section>
  );
}

export function AdminSchedulingWorkspaceRoute({
  subject,
  capabilityArea,
  view,
  anchorDate,
  readGateway,
  mutationGateway,
}: AdminSchedulingWorkspaceRouteProps) {
  const authorized = canReadAdminScheduling(subject, capabilityArea);
  const subjectKey = subject ? adminSchedulingAuthorizationKey(subject) : null;
  const [loadState, setLoadState] = useState<WorkspaceLoadState>({
    kind: "loading",
  });
  const [pendingAppointmentId, setPendingAppointmentId] =
    useState<AppointmentId | null>(null);
  const [optimisticAppointment, setOptimisticAppointment] = useState<
    AdminSchedulingWorkspaceSnapshot["appointments"][number] | null
  >(null);
  const [feedbackByAppointmentId, setFeedbackByAppointmentId] = useState<
    ReadonlyMap<AppointmentId, AdminSchedulingWorkspaceFeedback>
  >(() => new Map());
  const readSequence = useRef(0);
  const mutationSequence = useRef(0);
  const activeRead = useRef<AbortController | null>(null);
  const activeMutation = useRef<AbortController | null>(null);
  const focusReturn = useRef<{
    readonly appointmentId: AppointmentId;
    readonly actionId: string | null;
  } | null>(null);

  const load = useCallback(() => {
    if (!authorized || !readGateway || activeMutation.current) return;
    const sequence = readSequence.current + 1;
    readSequence.current = sequence;
    activeRead.current?.abort();
    const controller = new AbortController();
    activeRead.current = controller;
    const protectedStateGeneration = captureProtectedStateGeneration();
    setLoadState({ kind: "loading" });
    setFeedbackByAppointmentId(new Map());

    void readGateway({ anchorDate, view }, { signal: controller.signal }).then(
      (snapshot) => {
        if (
          controller.signal.aborted ||
          readSequence.current !== sequence ||
          !isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          return;
        }
        setLoadState(
          isAdminSchedulingWorkspaceSnapshot(snapshot) &&
            snapshot.anchorDate === anchorDate
            ? { kind: "ready", snapshot }
            : { kind: "error" },
        );
      },
      () => {
        if (
          !controller.signal.aborted &&
          readSequence.current === sequence &&
          isProtectedStateGenerationCurrent(protectedStateGeneration)
        ) {
          setLoadState({ kind: "error" });
        }
      },
    );
  }, [anchorDate, authorized, readGateway, view]);

  useEffect(() => {
    const clearState = (visible: boolean) => {
      readSequence.current += 1;
      mutationSequence.current += 1;
      activeRead.current?.abort();
      activeMutation.current?.abort();
      activeRead.current = null;
      activeMutation.current = null;
      if (visible) {
        setLoadState({ kind: "loading" });
        setPendingAppointmentId(null);
        setOptimisticAppointment(null);
        setFeedbackByAppointmentId(new Map());
      }
    };
    const unregister = registerProtectedStateCleaner(
      "admin-scheduling-workspace",
      () => clearState(true),
    );
    load();
    return () => {
      clearState(false);
      unregister();
    };
  }, [load, subjectKey]);

  const mutate = useCallback(
    (request: AdminSchedulingAppointmentMutationRequest) => {
      if (
        !canWriteAdminScheduling(subject, capabilityArea) ||
        !mutationGateway ||
        activeMutation.current ||
        loadState.kind !== "ready"
      ) {
        return;
      }
      const current = loadState.snapshot.appointments.find(
        (appointment) =>
          appointment.appointmentId === request.appointmentId &&
          appointment.revision === request.expectedRevision,
      );
      const idempotencyKey = createSchedulingIdempotencyKey();
      if (!current || !idempotencyKey) {
        setFeedbackByAppointmentId((feedback) => {
          const next = new Map(feedback);
          next.set(request.appointmentId, {
            tone: "critical",
            message:
              "The change could not be prepared. Refresh the schedule before trying again.",
            refreshRequired: true,
            undo: null,
          });
          return next;
        });
        return;
      }

      readSequence.current += 1;
      activeRead.current?.abort();
      activeRead.current = null;
      const sequence = mutationSequence.current + 1;
      mutationSequence.current = sequence;
      const controller = new AbortController();
      activeMutation.current = controller;
      const protectedStateGeneration = captureProtectedStateGeneration();
      setPendingAppointmentId(request.appointmentId);
      setOptimisticAppointment(
        previewSchedulingAppointmentMutation(current, request),
      );
      const activeElement =
        typeof document === "undefined" ? null : document.activeElement;
      focusReturn.current = {
        appointmentId: request.appointmentId,
        actionId:
          activeElement instanceof HTMLElement
            ? (activeElement.dataset.moveAction ?? null)
            : null,
      };
      setFeedbackByAppointmentId((feedback) => {
        const next = new Map(feedback);
        next.delete(request.appointmentId);
        return next;
      });

      void mutationGateway(request, {
        signal: controller.signal,
        idempotencyKey,
      })
        .then(
          (outcome) => {
            if (
              controller.signal.aborted ||
              mutationSequence.current !== sequence ||
              !isProtectedStateGenerationCurrent(protectedStateGeneration)
            ) {
              return;
            }
            if (outcome.kind !== "confirmed") {
              setOptimisticAppointment(null);
              setFeedbackByAppointmentId((feedback) => {
                const next = new Map(feedback);
                next.set(request.appointmentId, {
                  tone: "critical",
                  message:
                    outcome.kind === "conflict"
                      ? "The server rejected the target slot. The preview was rolled back to the confirmed appointment. Refresh before editing it."
                      : "The server did not confirm the change. The preview was rolled back. Refresh before trying again.",
                  refreshRequired: true,
                  undo: null,
                });
                return next;
              });
              return;
            }
            setLoadState((state) => {
              if (state.kind !== "ready") return state;
              const applied = applyConfirmedSchedulingAppointment(
                state.snapshot,
                current,
                outcome,
              );
              return applied ? { kind: "ready", snapshot: applied } : state;
            });
            const applied = applyConfirmedSchedulingAppointment(
              loadState.snapshot,
              current,
              outcome,
            );
            setFeedbackByAppointmentId((feedback) => {
              const next = new Map(feedback);
              next.set(request.appointmentId, {
                tone: applied ? "positive" : "critical",
                message: applied
                  ? outcome.undo
                    ? "The scheduling change was confirmed. Undo is available for five minutes if the previous slot remains free."
                    : "The scheduling change was confirmed."
                  : "The response could not be reconciled. Refresh the schedule.",
                refreshRequired: !applied,
                undo: applied ? outcome.undo : null,
              });
              return next;
            });
            setOptimisticAppointment(null);
          },
          () => {
            if (
              !controller.signal.aborted &&
              mutationSequence.current === sequence &&
              isProtectedStateGenerationCurrent(protectedStateGeneration)
            ) {
              setFeedbackByAppointmentId((feedback) => {
                const next = new Map(feedback);
                next.set(request.appointmentId, {
                  tone: "critical",
                  message:
                    "The scheduling change could not be confirmed. Refresh before trying again.",
                  refreshRequired: true,
                  undo: null,
                });
                return next;
              });
            }
          },
        )
        .finally(() => {
          if (
            mutationSequence.current === sequence &&
            isProtectedStateGenerationCurrent(protectedStateGeneration)
          ) {
            activeMutation.current = null;
            setPendingAppointmentId(null);
            setOptimisticAppointment(null);
            const target = focusReturn.current;
            focusReturn.current = null;
            if (target && typeof document !== "undefined") {
              requestAnimationFrame(() => {
                const card = document.getElementById(
                  `schedule-${target.appointmentId}`,
                );
                const action = target.actionId
                  ? card?.querySelector<HTMLElement>(
                      `[data-move-action="${target.actionId}"]`,
                    )
                  : null;
                (action ?? card?.querySelector<HTMLElement>("h3"))?.focus();
              });
            }
          }
        });
    },
    [capabilityArea, loadState, mutationGateway, subject],
  );

  const undo = useCallback(
    (appointmentId: AppointmentId, grant: AdminSchedulingUndoGrant) => {
      if (loadState.kind !== "ready") return;
      const current = loadState.snapshot.appointments.find(
        (appointment) => appointment.appointmentId === appointmentId,
      );
      if (!current) return;
      mutate({
        kind: "UNDO",
        appointmentId,
        expectedRevision: current.revision,
        restoreRevision: grant.restoreRevision,
        token: grant.token,
      });
    },
    [loadState, mutate],
  );

  if (!authorized) {
    return (
      <WorkspaceFrame title="Schedule unavailable">
        <ContentState kind="warning" title="Access denied" live={false}>
          Schedule data is only available with the schedule read permission.
        </ContentState>
      </WorkspaceFrame>
    );
  }

  if (!readGateway) {
    return (
      <WorkspaceFrame title="Schedule unavailable">
        <ContentState
          kind="warning"
          title="Scheduling connection unavailable"
          live={false}
        >
          No production scheduling gateway is configured. No local or synthetic
          data was substituted.
        </ContentState>
      </WorkspaceFrame>
    );
  }

  if (loadState.kind === "loading") {
    return (
      <WorkspaceFrame title="Loading schedule">
        <ContentState kind="loading" title="Loading confirmed schedule data" />
      </WorkspaceFrame>
    );
  }

  if (loadState.kind === "error") {
    return (
      <WorkspaceFrame title="Schedule unavailable">
        <ContentState
          kind="error"
          title="Confirmed schedule data could not be loaded"
          actions={
            <button type="button" onClick={load}>
              Retry
            </button>
          }
        >
          The workspace remains closed until a valid scheduling response is
          available.
        </ContentState>
      </WorkspaceFrame>
    );
  }

  return (
    <AdminSchedulingWorkspaceView
      view={view}
      snapshot={applySchedulingPreview(
        loadState.snapshot,
        optimisticAppointment,
      )}
      canWrite={
        canWriteAdminScheduling(subject, capabilityArea) &&
        mutationGateway !== null
      }
      pendingAppointmentId={pendingAppointmentId}
      optimisticAppointmentId={optimisticAppointment?.appointmentId ?? null}
      workspaceBusy={pendingAppointmentId !== null}
      feedbackByAppointmentId={feedbackByAppointmentId}
      onMutationRequest={mutate}
      onRefresh={load}
      onUndo={undo}
    />
  );
}
